import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { AppLayout } from '../components/AppLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Loader2, Check, User, Shield, Zap, CreditCard, Camera, ArrowRight } from 'lucide-react';
import { Toaster, toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const Profile = () => {
  const { user, profile, currentPath, session, updateProfile, isAdmin, getAuthHeaders } = useAuth();
  const [displayName, setDisplayName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [cancelingPlan, setCancelingPlan] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  const defaultAvatar = `https://api.dicebear.com/9.x/notionists/svg?seed=${user?.email || 'user'}&backgroundColor=f5f5f5`;

  useEffect(() => {
    if (profile) {
      setDisplayName(profile.display_name || '');
    }
  }, [profile]);

  useEffect(() => {
    const fetchSubscription = async () => {
      const token = session?.access_token;
      if (!token) return;
      try {
        const res = await fetch(`${API}/subscription/status`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (res.ok) setSubscription(await res.json());
      } catch (e) {
        process.env.NODE_ENV === "development" && console.error('Error fetching subscription:', e);
      }
    };
    fetchSubscription();
  }, [session]);

  const handleCancelPlan = async () => {
    const token = session?.access_token;
    if (!token) return;
    setCancelingPlan(true);
    try {
      const res = await fetch(`${API}/subscription/cancel`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        setSubscription(prev => prev ? { ...prev, plan_id: 'free', plan_name: 'Free', monthly_generations: 2 } : prev);
        toast.success('Subscription canceled. You are now on the Free plan.');
      }
    } catch (e) {
      toast.error('Failed to cancel subscription');
    } finally {
      setCancelingPlan(false);
    }
  };

  const handleSave = async () => {
    if (!displayName.trim()) {
      toast.error('Display name is required');
      return;
    }

    setIsSaving(true);
    try {
      await updateProfile({
        display_name: displayName.trim(),
        avatar_url: profile?.avatar_url || defaultAvatar
      });
      toast.success('Profile updated');
    } catch (err) {
      toast.error('Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be under 2MB');
      return;
    }

    setUploadingAvatar(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(`${API}/profile/avatar`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData
      });
      if (response.ok) {
        const data = await response.json();
        await updateProfile({ avatar_url: data.avatar_url });
        toast.success('Avatar updated');
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to upload avatar');
      }
    } catch (err) {
      toast.error('Failed to upload avatar');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleResetAvatar = async () => {
    await updateProfile({ avatar_url: defaultAvatar });
    toast.success('Avatar reset to default');
  };

  return (
    <AppLayout maxWidth="max-w-2xl">
      <Toaster position="top-center" richColors />
      
      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-foreground" data-testid="profile-title">Settings</h1>
        <p className="text-sm text-muted-foreground">Manage your profile</p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-5"
      >
        {/* Profile Card */}
        <Card className="border-border border-t-4 border-t-teal-500" data-testid="profile-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <User className="h-4 w-4" />
              Your Profile
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Avatar + info */}
            <div className="flex items-center gap-4">
              <div className="relative group">
                <img
                  src={profile?.avatar_url || defaultAvatar}
                  alt="Avatar"
                  className="w-14 h-14 rounded-full bg-secondary object-cover ring-2 ring-teal-500 ring-offset-2 ring-offset-background"
                  data-testid="profile-avatar"
                />
                <label className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                  {uploadingAvatar ? (
                    <Loader2 className="h-4 w-4 text-white animate-spin" />
                  ) : (
                    <Camera className="h-4 w-4 text-white" />
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarUpload}
                    className="hidden"
                    data-testid="avatar-upload-input"
                  />
                </label>
              </div>
              <div>
                <p className="font-medium text-foreground">{profile?.display_name || 'Your Name'}</p>
                <p className="text-sm text-muted-foreground">{user?.email}</p>
                <div className="flex items-center gap-2 mt-1">
                  {isAdmin && (
                    <span className="inline-flex items-center text-xs text-teal-400 bg-teal-700/15 px-2 py-0.5 rounded">
                      <Shield className="h-3 w-3 mr-1" />
                      Admin
                    </span>
                  )}
                  {profile?.avatar_url && profile.avatar_url !== defaultAvatar && (
                    <button
                      onClick={handleResetAvatar}
                      className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                      data-testid="reset-avatar-btn"
                    >
                      Reset avatar
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="displayName" className="text-sm font-medium">
                  Display Name
                </Label>
                <Input
                  id="displayName"
                  type="text"
                  placeholder="Your name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  className="h-10 border-border"
                  data-testid="profile-display-name-input"
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-muted-foreground">
                  Email
                </Label>
                <Input
                  type="email"
                  value={user?.email || ''}
                  disabled
                  className="h-10 border-border bg-secondary/50 cursor-not-allowed"
                  data-testid="profile-email-input"
                />
                <p className="text-xs text-muted-foreground">
                  Email cannot be changed
                </p>
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <Button
                onClick={handleSave}
                disabled={isSaving}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="profile-save-btn"
              >
                {isSaving ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</>
                ) : (
                  <><Check className="mr-2 h-4 w-4" />Save Changes</>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Assigned Path */}
        {currentPath?.path_id && (
          <Card className="border-border border-t-4 border-t-emerald-500" data-testid="path-info-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Your Path</CardTitle>
              <CardDescription className="text-sm">
                You can switch paths by retaking the quiz
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="font-medium text-foreground text-sm">
                    {currentPath.path_id === 'path_fast_cash' && 'Fast Cash Path'}
                    {currentPath.path_id === 'path_digital_product' && 'Simple Digital Product Path'}
                    {currentPath.path_id === 'path_faceless_affiliate' && 'Faceless Affiliate Path'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Currently on step {currentPath.current_step_number}
                  </p>
                </div>
                <Link to="/onboarding">
                  <Button variant="outline" size="sm" className="text-xs">Switch Path</Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Subscription / Billing */}
        <Card className="border-border border-t-4 border-t-indigo-500" data-testid="billing-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <CreditCard className="h-4 w-4" />
              Subscription
            </CardTitle>
          </CardHeader>
          <CardContent>
            {subscription ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 bg-teal-700/15 rounded-lg flex items-center justify-center">
                      <Zap className="h-4 w-4 text-teal-400" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground text-sm">{subscription.plan_name} Plan</p>
                      <p className="text-xs text-muted-foreground">
                        {subscription.monthly_generations === -1
                          ? 'Unlimited generations'
                          : `${subscription.generations_used}/${subscription.monthly_generations} used this month`}
                      </p>
                    </div>
                  </div>
                  {subscription.plan_id === 'free' ? (
                    <Link to="/pricing">
                      <Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90 text-xs" data-testid="settings-upgrade-btn">
                        Upgrade
                      </Button>
                    </Link>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs"
                      onClick={handleCancelPlan}
                      disabled={cancelingPlan}
                      data-testid="cancel-plan-btn"
                    >
                      {cancelingPlan ? 'Canceling...' : 'Cancel Plan'}
                    </Button>
                  )}
                </div>
                <Link to="/settings/billing" className="block">
                  <Button variant="ghost" size="sm" className="w-full justify-between text-xs text-teal-700 hover:bg-teal-50" data-testid="settings-manage-billing-btn">
                    Manage billing & plan
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">Free Plan (2 generations/mo)</p>
                <Link to="/pricing">
                  <Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90 text-xs">Upgrade</Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </AppLayout>
  );
};

export default Profile;
